import React, { useState, useRef, useEffect } from "react";
import VideoProvider from "../context/VideoContext";
import ScrollingCards from "../components/ScrollingCards";
import AccountLockedMessage from "./AccountLockedMessage";
import AccountExpiredMessage from "./AccountExpiredMessage";
import "../App.css";
import "../styles/PasswordProtection.css";
import tableData from '../data/playlistlink.json';
import SupportUs from '../components/SupportUs';
import DynamicTable from '../components/DynamicTable';
import startDevToolsDetection from '../services/devToolsDetection';
import { sendOtpRequest, verifyOtpRequest } from '../services/otpService.js';
import { UserRequest } from "../models/UserRequest.js";
import { accountLockStatus } from "../services/accountService.js";
import { getUserIp } from '../services/getUserLocation.js';
import LocalDateTime, { getUserTimeZone } from '../components/localDateTime';

const API_KEY = "aVo39j49q6w5z8hu5hzuyo36qckf1drq1y!s";

const PasswordProtection = ({ onPasswordSuccess }) => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("email"); // 'email' or 'otp'
  const [error, setError] = useState("");
  const [errorIpBlocked, setErrorIpBlocked] = useState("");
  const [loading, setLoading] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [accessExpired, setAccessExpired] = useState(false);
  const otpInputRef = useRef(null);

  useEffect(() => {
    startDevToolsDetection(); // Start DevTools detection when the app mounts
    document.getElementById("email")?.focus();
  }, []);

  const isEmailValid = email.includes("@gmail.com");
  const isOtpValid = otp.length === 6;

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      if (step === "email" && isEmailValid) {
        handleSendOtp();
      } else if (step === "otp" && isOtpValid) {
        handleVerifyOtp();
      }
    }
  };

  // Handle sending OTP to user's email
  const handleSendOtp = async () => {
    setLoading(true);
    setError("");
    setErrorIpBlocked("");
    try {
      const ip = await getUserIp();
      const requestData = new UserRequest(email + "," + ip + "," + getUserTimeZone(), API_KEY, false, "sendOTP");
      const apiResponse = await sendOtpRequest(requestData);
      if (apiResponse.success && apiResponse.data.message === "OTP sent to your email.") {
        setStep("otp");
      } else if (apiResponse.success && apiResponse.data.statusText === "your IP is blocked") {
        setErrorIpBlocked(true);
      } else {
        setError(true);
        setLoading(false);
        return;
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (step === "otp") {
      otpInputRef.current?.focus();
    }
  }, [step]);


  // Handle verifying OTP
  const handleVerifyOtp = async () => {
    setLoading(true);
    setError("");
    setErrorIpBlocked("");
    try {
      const ip = await getUserIp();
      const requestData = new UserRequest(email + "," + ip + "," + getUserTimeZone(), API_KEY, false, "verifyOTP", otp);
      const apiResponse = await verifyOtpRequest(requestData);
      if (apiResponse.success && apiResponse.data.statusText === "OK") {
        onPasswordSuccess(email);
      } else {
        setError(true);
        setLoading(false);
        return;
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div>
      {loading && (
        <div className="preloader-container">
          <div className="loader"></div>
        </div>
      )}

      <div className="scrolling-cards-container">
        <VideoProvider>
          <ScrollingCards />
        </VideoProvider>
      </div>

      <div className="container" style={{ textAlign: "center", padding: "20px" }}>
        <LocalDateTime />
        <h1>Welcome to Practice Platform Website</h1>
        <p class="tech-stack fade-in">
          <strong>Tech Stack:</strong> Built with <strong>free tools.</strong>&nbsp;&nbsp;  
          <em>React.js • Google Apps Script & Sheets API • Email OTP • GitHub Pages (except domain)</em>
        </p>
        <p>
          {/* {step === "email" ? (
            "Type in your email and click Submit — we’ll send an OTP to your inbox right away."
          ) : (
            <>
              We’ve sent an OTP to your{" "}
              <span className="highlight-email">{email}</span>. Enter it below and click Continue — it’s valid for 5 minutes.
            </>
          )} */}
        </p>

        <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
          <input type="text" name="fake_email" autoComplete="off" style={{ display: "none" }} />

          {/* Email input field */}
          {step === "email" && (
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter your Gmail address"
              className="auth-input"
              // disabled={loading || isBlocked || accessExpired}
              disabled="true"
              onCopy={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              autoComplete="off"
            />
          )}

          {step === "otp" && (
            <input
              ref={otpInputRef}  // 👈 Attach ref here
              id="password"
              type="password"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter OTP that is received in your email"
              className="auth-input"
              disabled={loading || isBlocked || accessExpired}
              onCopy={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              autoComplete="off"
              maxLength={6}
            />
          )}

          {/* Submit button */}
          <button
            type="button"
            onClick={step === "email" ? handleSendOtp : handleVerifyOtp}
            disabled={(step === "email" && !isEmailValid) || (step === "otp" && !isOtpValid) || loading || isBlocked || accessExpired}
          >
            {step === "email" ? "Submit" : "Continue"}
          </button>
        </form>
        {/* {step === "email" && <p className="support-text">Only Gmail addresses are supported at this time.</p>} */}
        {step === "email" && <p className="support-text">Project on hold for improvements. Access is restricted—comment on our latest video to request access.</p>}
        {errorIpBlocked && (
          <p className="error-ip-blocked">
            You've reached today's limit.<br /><br />
            We noticed multiple attempts from your device using different emails after reaching the daily quota.
            To keep things fair for everyone, we limit usage per day.<br /><br />
            This platform takes a lot of effort to build and maintain. If it’s helping you, please consider supporting us — it means a lot and helps keep the site free for all.<br /><br />
            Want to keep practicing right now? Support us with a coffee link below, and we’ll unlock unlimited access just for you — no waiting, no limits.
            <br /><br />Otherwise, feel free to come back tomorrow and continue for free.
            Thank you — and best of luck on your PTE journey!
            <b><SupportUs /> </b>{/* This will display the message */}
          </p>
        )}
        {error && <p style={{ color: 'red' }}>Oops! The OTP is incorrect, invalid, or may have expired.</p>}
        {isBlocked && <AccountLockedMessage />}
        {accessExpired && <AccountExpiredMessage />}

        <br />
        <div>
          <DynamicTable data={tableData} />
        </div>
      </div>
    </div>
  );
};

export default PasswordProtection;
